import { equals, identity, partial } from 'ramda';
import * as React from 'react';
import { createStore, useStore } from 'zustand';
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient';
import type { FeatureFlagClient, FeatureFlagsStore } from '@/scopes/common/domain/types';
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common';
import type { FeatureFlagId } from '@/scopes/common/infrastructure/feature-flags/featureFlags';

featureFlagClient.getActiveList();
export function createFeatureFlagsStore(client: FeatureFlagClient<FeatureFlagId> = featureFlagClient) {
  return createStore<FeatureFlagsStore>((set) => ({
    featureList: client.getAll(),
    activeFeaturesList: client.getActiveList(),
    toggleFeature: (id) => {
      client.toggle(id);
      set((state) => ({
        ...state,
        activeFeaturesList: client.getActiveList(),
      }));
    },
  }));
}

export const useFeatureFlag = (id: FeatureFlagId) => {
  const store = useFeatureFlagStore();

  return isFeatureActiveSelector(id)(store);
};

export const useFeatureList = () => {
  const store = useFeatureFlagStore();

  // eslint-disable-next-line react-hooks/exhaustive-deps -- featureList should not change on a same build
  return React.useMemo(() => Object.keys(store.featureList) as [FeatureFlagId], []);
};

export const useFeatureFlagController = (id: FeatureFlagId) => {
  const store = useFeatureFlagStore();
  const { toggleFeature } = store;
  const isActive = useFeatureFlag(id);
  const feature = store.featureList[id];

  return {
    isActive,
    feature,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Rambda partial is well know
    toggleFeature: React.useCallback(partial(toggleFeature, [id]), [toggleFeature, id]),
  };
};

export default {
  client: featureFlagClient,
  store: createFeatureFlagsStore(),
};

function isFeatureActiveSelector(id: FeatureFlagId) {
  return ({ activeFeaturesList }: FeatureFlagsStore) => Boolean(activeFeaturesList.find(equals(id)));
}

function useFeatureFlagStore() {
  return useStore(useCommonDependency('featureFlagsStore'), identity<FeatureFlagsStore>);
}
